import Modal from "../../../../../assets/js/components/Modal";
import { createSortArrows, formatDate } from "features/discover/functions";
import { renderBoolean } from "features/datagrid-configs/cellRenderers";
import client from "libs/client";
import { _ } from "i18n";
import http from "libs/http";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { Link } from "react-router";
import { useToast } from "ui-kit";
import { PaginationBar } from "ui-kit/src/PaginationControls/PaginationBar";

export type DiscoverSubscriptionListItem = {
	id: number;
	teamId: number;
	teamName: string;
	clubId: number | null;
	clubName: string | null;
	productName: string;
	isActive: boolean;
	tokenPrice: number;
	isFree: boolean;
	status: string;
	renewalStatus: string;
	startDate: string;
	expirationDate: string;
	subscriberType: string;
	comment: string | null;
};

type DiscoverSubscriptionsListResponse = {
	items: DiscoverSubscriptionListItem[];
	tokensSum: number;
	page: number;
	limit: number;
	pages: number;
	total: number;
};

type SubscriptionSortState = { field: string; direction: string };

const PAGE_SIZE = 10;

const buildSubscriptionsRequestUrl = (baseUrl: string, query: Record<string, string | number>): string => {
	const params = new URLSearchParams();

	for (const [key, value] of Object.entries(query)) {
		params.append(key, String(value));
	}

	const queryString = params.toString();

	return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

type SubscriptionKindEnum = "paymentType" | "status" | "renewal";

type SubscriptionActionEnum =
	| "setFree"
	| "setPaid"
	| "activate"
	| "stop"
	| "activateRenewal"
	| "pauseRenewal";

type SubscriptionModalState = {
	kind: SubscriptionKindEnum;
	subscription: DiscoverSubscriptionListItem;
	action: SubscriptionActionEnum;
};

const renderSubscriber = (subscription: DiscoverSubscriptionListItem) => {
	if (subscription.subscriberType === "team") {
		return (
			<>
				<Link to={`/discover/team/${subscription.teamId}`}>
					<strong>{subscription.teamName}</strong>
				</Link>
				<small style={{ fontWeight: "bold", fontSize: "10px" }}> (team)</small>
			</>
		);
	}

	if (subscription.subscriberType === "clubhouse" && subscription.clubId !== null) {
		return (
			<>
				<Link to={`/discover/club/${subscription.clubId}`}>
					<strong>{subscription.clubName ?? "clubhouse"}</strong>
				</Link>
				<small style={{ fontWeight: "bold", fontSize: "10px" }}> (clubhouse)</small>
			</>
		);
	}

	return subscription.subscriberType;
};

const renderClickableLabel = (label: string, color: string, onClick: () => void) => (
	<button
		type="button"
		className="button-link"
		style={{ color, fontWeight: "bold", padding: 0, border: "none", background: "none" }}
		onClick={onClick}
	>
		{label}
	</button>
);

const renderStatusLabel = (label: string, color: string, onClick: () => void, subscriptionEditAccess: boolean) => {
	if (!subscriptionEditAccess) {
		return <span style={{ color, fontWeight: "bold" }}>{label}</span>;
	}

	return renderClickableLabel(label, color, onClick);
};

const getModalTitle = (modal: SubscriptionModalState | null): string => {
	if (modal?.kind === "status") {
		return "Subscription status";
	}

	if (modal?.kind === "renewal") {
		return "Subscription renewal";
	}

	return "Subscription payment type";
};

const getModalContent = (modal: SubscriptionModalState | null): { text: string; confirmLabel: string } => {
	if (modal === null) {
		return { text: "", confirmLabel: "" };
	}

	if (modal.kind === "paymentType") {
		return {
			text: modal.action === "setPaid"
				? "Do you want to set the subscription to paid?"
				: "Do you want to set the subscription to free?",
			confirmLabel: modal.action === "setPaid" ? "Set to paid" : "Set to free",
		};
	}

	if (modal.kind === "status") {
		return {
			text: modal.action === "stop"
				? "Do you want to stop the subscription?"
				: "Do you want to activate the subscription?",
			confirmLabel: modal.action === "stop" ? "Stop subscription" : "Activate subscription",
		};
	}

	return {
		text: modal.action === "pauseRenewal"
			? "Do you want to pause the subscription renewal?"
			: "Do you want to activate the subscription renewal?",
		confirmLabel: modal.action === "pauseRenewal" ? "Pause renewal" : "Activate Renewal",
	};
};

type DiscoverSubscriptionsTableProps = {
	subscriptionEditAccess: boolean;
} & (
	| { teamId: number; clubId?: never }
	| { clubId: number; teamId?: never }
);

export function DiscoverSubscriptionsTable({ teamId, clubId, subscriptionEditAccess }: DiscoverSubscriptionsTableProps) {
	const { addSuccessToast, addErrorToast } = useToast();
	const [modal, setModal] = useState<SubscriptionModalState | null>(null);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState("");
	const [isFree, setIsFree] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [sort, setSort] = useState<SubscriptionSortState>();
	const [pageIndex, setPageIndex] = useState(0);

	const subscriptionsUrl = teamId !== undefined
		? `/admin/discover/subscriptions/team/${teamId}`
		: `/admin/discover/subscriptions/club/${clubId}`;

	const queryKey = teamId !== undefined
		? ["discoverTeamSubscriptions", teamId]
		: ["discoverClubSubscriptions", clubId];

	const { data, isLoading, refetch } = useQuery({
		queryKey: [...queryKey, pageIndex, sort],
		queryFn: () => {
			const query: Record<string, string | number> = {
				page: pageIndex + 1,
				limit: PAGE_SIZE,
			};

			if (sort !== undefined) {
				query["order[field]"] = sort.field;
				query["order[direction]"] = sort.direction;
			}

			return http
				.url(buildSubscriptionsRequestUrl(subscriptionsUrl, query))
				.get()
				.json<DiscoverSubscriptionsListResponse>();
		},
	});

	const closeModal = () => {
		setModal(null);
		setIsSubmitting(false);
	};

	const closeAddModal = () => {
		setIsAddModalOpen(false);
		setSelectedProductId("");
		setIsFree(false);
		setIsSubmitting(false);
	};

	const { data: productsData, isLoading: isProductsLoading } = client.adminDiscoverSubscriptionProductsGet.useQuery({
		enabled: teamId !== undefined && subscriptionEditAccess,
	});

	const productOptions = productsData?.data?.items ?? [];

	const setToFreeMutation = client.adminDiscoverSubscriptionSetToFreePost.useMutation({
		onSuccess: () => {
			addSuccessToast("Subscription set to free");
			closeModal();
			refetch();
		},
		onError: (err) => {
			addErrorToast(err.message || "Failed to set subscription to free");
			setIsSubmitting(false);
		},
	});

	const setToPaidMutation = client.adminDiscoverSubscriptionSetToPaidPost.useMutation({
		onSuccess: () => {
			addSuccessToast("Subscription set to paid");
			closeModal();
			refetch();
		},
		onError: (err) => {
			addErrorToast(err.message || "Failed to set subscription to paid");
			setIsSubmitting(false);
		},
	});

	const activateMutation = client.adminDiscoverSubscriptionActivatePost.useMutation({
		onSuccess: () => {
			addSuccessToast("Subscription activated");
			closeModal();
			refetch();
		},
		onError: (err) => {
			addErrorToast(err.message || "Failed to activate subscription");
			setIsSubmitting(false);
		},
	});

	const stopMutation = client.adminDiscoverSubscriptionStopPost.useMutation({
		onSuccess: () => {
			addSuccessToast("Subscription stopped");
			closeModal();
			refetch();
		},
		onError: (err) => {
			addErrorToast(err.message || "Failed to stop subscription");
			setIsSubmitting(false);
		},
	});

	const activateRenewalMutation = client.adminDiscoverSubscriptionActivateRenewalPost.useMutation({
		onSuccess: () => {
			addSuccessToast("Subscription renewal activated");
			closeModal();
			refetch();
		},
		onError: (err) => {
			addErrorToast(err.message || "Failed to activate subscription renewal");
			setIsSubmitting(false);
		},
	});

	const pauseRenewalMutation = client.adminDiscoverSubscriptionPauseRenewalPost.useMutation({
		onSuccess: () => {
			addSuccessToast("Subscription renewal paused");
			closeModal();
			refetch();
		},
		onError: (err) => {
			addErrorToast(err.message || "Failed to pause subscription renewal");
			setIsSubmitting(false);
		},
	});

	const addSubscriptionMutation = client.adminDiscoverSubscriptionByTeamPost.useMutation({
		onSuccess: () => {
			addSuccessToast("Subscription added");
			closeAddModal();
			refetch();
		},
		onError: (err) => {
			addErrorToast(err.message || "Failed to add subscription");
			setIsSubmitting(false);
		},
	});

	const subscriptions = data?.items ?? [];
	const tokensSum = data?.tokensSum ?? 0;
	const pageCount = data?.pages ?? 0;
	const total = data?.total ?? 0;

	const handleSort = (newSort?: SubscriptionSortState) => {
		setSort(newSort);
		setPageIndex(0);
	};

	const handlePageChange = (page: number) => {
		setPageIndex(page - 1);
	};

	const renderSortableHeader = (field: string, label: string, hasSort = true) => (
		<th>
			<div className="tableHeader">
				<span>{label}</span>
				{hasSort ? createSortArrows(field, sort, handleSort) : null}
			</div>
		</th>
	);

	const openPaymentTypeModal = (subscription: DiscoverSubscriptionListItem) => {
		setModal({
			kind: "paymentType",
			subscription,
			action: subscription.isFree ? "setPaid" : "setFree",
		});
	};

	const openStatusModal = (subscription: DiscoverSubscriptionListItem) => {
		setModal({
			kind: "status",
			subscription,
			action: subscription.status === "active" ? "stop" : "activate",
		});
	};

	const openRenewalModal = (subscription: DiscoverSubscriptionListItem) => {
		setModal({
			kind: "renewal",
			subscription,
			action: subscription.renewalStatus === "active" ? "pauseRenewal" : "activateRenewal",
		});
	};

	const handleConfirm = () => {
		if (modal === null) {
			return;
		}

		setIsSubmitting(true);
		const subscriptionId = modal.subscription.id;

		if (modal.kind === "paymentType") {
			if (modal.action === "setFree") {
				setToFreeMutation.mutate({ subscriptionId });
				return;
			}

			setToPaidMutation.mutate({ subscriptionId });
			return;
		}

		if (modal.kind === "status") {
			if (modal.action === "activate") {
				activateMutation.mutate({ subscriptionId });
				return;
			}

			stopMutation.mutate({ subscriptionId });
			return;
		}

		if (modal.action === "activateRenewal") {
			activateRenewalMutation.mutate({ subscriptionId });
			return;
		}

		pauseRenewalMutation.mutate({ subscriptionId });
	};

	const handleAddSubscription = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (teamId === undefined || selectedProductId === "") {
			return;
		}

		setIsSubmitting(true);
		addSubscriptionMutation.mutate({
			productId: Number(selectedProductId),
			teamId,
			isFree: (isFree ? "1" : "0") as unknown as boolean,
		});
	};

	if (isLoading) {
		return <div className="empty-sub-table">Loading...</div>;
	}

	const { text: modalText, confirmLabel } = getModalContent(modal);

	return (
		<>
			<div
				style={{
					marginBottom: "1rem",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<div>
					{teamId !== undefined
						? "Total tokens paid by team per month:"
						: "Total tokens paid by club per month:"}{" "}
					<strong style={{ fontSize: "1.3em", paddingLeft: "1rem" }}>{tokensSum}</strong>
				</div>
				{teamId !== undefined && subscriptionEditAccess && (
					<button type="button" onClick={() => setIsAddModalOpen(true)}>
						Add subscription
					</button>
				)}
			</div>

			{total === 0 ? (
				<div className="empty-sub-table">No subscriptions</div>
			) : (
			<>
			<div className="discover-subscriptions-table">
				<table className="discover-table">
					<thead>
						<tr>
							{renderSortableHeader("id", "ID")}
							{renderSortableHeader("productName", "Product")}
							{renderSortableHeader("isActive", "Active")}
							{renderSortableHeader("tokenPrice", "Token price")}
							{renderSortableHeader("isFree", "Free")}
							{renderSortableHeader("status", "Status")}
							{renderSortableHeader("renewalStatus", "Renewal")}
							{renderSortableHeader("startAt", "Start at")}
							{renderSortableHeader("expirationAt", "Expiration")}
							{renderSortableHeader("subscriberType", "Subscriber")}
							{renderSortableHeader("comment", "Comment")}
						</tr>
					</thead>
					<tbody>
						{subscriptions.map((subscription) => {
							const isStatusActive = subscription.status === "active";
							const isRenewalActive = subscription.renewalStatus === "active";
							const freeLabel = subscription.isFree ? _("label.datagrid.yes") : _("label.datagrid.no");
							const freeColor = subscription.isFree ? "green" : "red";

							return (
								<tr key={subscription.id}>
									<td>{subscription.id}</td>
									<td>{subscription.productName}</td>
									<td>{renderBoolean(subscription.isActive)}</td>
									<td>{subscription.tokenPrice}</td>
									<td>
										{renderStatusLabel(freeLabel, freeColor, () => openPaymentTypeModal(subscription), subscriptionEditAccess)}
									</td>
									<td>
										{renderStatusLabel(
											subscription.status,
											isStatusActive ? "green" : "red",
											() => openStatusModal(subscription),
											subscriptionEditAccess,
										)}
									</td>
									<td>
										{renderStatusLabel(
											subscription.renewalStatus,
											isRenewalActive ? "green" : "red",
											() => openRenewalModal(subscription),
											subscriptionEditAccess,
										)}
									</td>
									<td>{formatDate(subscription.startDate)}</td>
									<td>{formatDate(subscription.expirationDate)}</td>
									<td>{renderSubscriber(subscription)}</td>
									<td className="discover-subscriptions-table__comment">{subscription.comment ?? "-"}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<PaginationBar
				totalPages={pageCount}
				currentPage={pageIndex + 1}
				onPageChange={handlePageChange}
			/>
			</>
			)}

			{modal !== null && (
				<Modal
					show={true}
					title={getModalTitle(modal)}
					size="sm"
					onClose={() => !isSubmitting && closeModal()}
				>
					<p style={{ marginBottom: "1.5rem" }}>{modalText}</p>
					<div className="admin-buttons admin-buttons--right">
						<button
							type="button"
							className="admin-button admin-button--primary"
							disabled={isSubmitting}
							onClick={handleConfirm}
						>
							{confirmLabel}
						</button>
						<button
							type="button"
							className="admin-button admin-button--secondary"
							disabled={isSubmitting}
							onClick={closeModal}
						>
							Cancel
						</button>
					</div>
				</Modal>
			)}

			{isAddModalOpen && teamId !== undefined && (
				<Modal
					show={true}
					title="Add subscription"
					size="sm"
					onClose={() => !isSubmitting && closeAddModal()}
				>
					<form onSubmit={handleAddSubscription}>
						<div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
							<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
								<label htmlFor="subscription-product" style={{ width: "120px" }}>
									Product <span style={{ color: "red" }}>*</span>
								</label>
								<select
									id="subscription-product"
									value={selectedProductId}
									onChange={(e) => setSelectedProductId(e.target.value)}
									required
									disabled={isProductsLoading || isSubmitting}
									style={{ flex: 1 }}
								>
									<option value="">
										{isProductsLoading ? "Loading..." : "Select product"}
									</option>
									{productOptions.map((product) => (
										<option key={product.id} value={product.id}>
											{product.name} ({product.tokenPrice} tokens)
										</option>
									))}
								</select>
							</div>

							<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
								<label htmlFor="subscription-free" style={{ width: "120px" }}>
									Free
								</label>
								<input
									id="subscription-free"
									type="checkbox"
									checked={isFree}
									onChange={(e) => setIsFree(e.target.checked)}
									disabled={isSubmitting}
								/>
							</div>

						</div>
						<div className="admin-buttons admin-buttons--right">
							<button
								type="submit"
								className="admin-button admin-button--primary"
								disabled={isSubmitting || isProductsLoading}
							>
								Add subscription
							</button>
							<button
								type="button"
								className="admin-button admin-button--secondary"
								disabled={isSubmitting}
								onClick={closeAddModal}
							>
								Cancel
							</button>
						</div>
					</form>
				</Modal>
			)}
		</>
	);
}
